import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Types of music services supported
export type MusicServiceType = 'spotify' | 'apple';

// Context type definition
interface MusicServiceContextType {
  currentService: MusicServiceType;
  setCurrentService: (service: MusicServiceType) => void;
  isSpotifySelected: boolean;
  isAppleSelected: boolean;
}

// Create context with default values
const MusicServiceContext = createContext<MusicServiceContextType>({
  currentService: 'spotify',
  setCurrentService: () => {
    console.warn('MusicServiceContext not initialized');
  },
  isSpotifySelected: true,
  isAppleSelected: false,
});

// Provider component
export function MusicServiceProvider({ children }: { children: ReactNode }) {
  // Get saved preference from localStorage or default to Spotify
  const [currentService, setCurrentService] = useState<MusicServiceType>(
    () => (localStorage.getItem('preferred_music_service') as MusicServiceType) || 'spotify'
  );

  // Update localStorage when service changes
  useEffect(() => {
    localStorage.setItem('preferred_music_service', currentService);
  }, [currentService]);

  const isSpotifySelected = currentService === 'spotify';
  const isAppleSelected = currentService === 'apple';

  const contextValue = {
    currentService,
    setCurrentService,
    isSpotifySelected,
    isAppleSelected,
  };

  return (
    <MusicServiceContext.Provider value={contextValue}>
      {children}
    </MusicServiceContext.Provider>
  );
}

// Hook for consuming the context
export function useMusicService() {
  return useContext(MusicServiceContext);
} 